import {
  collection,
  type DocumentChange,
  type FirestoreError,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  type QuerySnapshot,
  type Unsubscribe,
  where,
  Timestamp,
} from 'firebase/firestore';
import { getFirebaseFirestore } from '@lib/config/firebase';

export interface AppReviewNotification {
  id: string;
  message?: string;
  timestamp: number;
  status?: 'read' | 'unread';
}

export interface AppReviewListenerConfig {
  userId: string;
  onNotification?: (notification: AppReviewNotification) => void;
  onError?: (error: FirestoreError) => void;
}

export interface AppReviewListener {
  unsubscribe: () => void;
}

export async function createAppReviewListener(
  config: AppReviewListenerConfig
): Promise<AppReviewListener> {
  const { userId, onNotification, onError } = config;

  try {
    const db = getFirebaseFirestore();
    const collectionPath = `users/${userId}/appReviewNotifications`;
    const notificationsCollection = collection(db, collectionPath);

    console.log('Setting up app review notification listener:', {
      userId,
      collectionPath,
      firestoreApp: db.app.name,
    });

    let isActive = true; // Flag to check if listener is still active
    let onSnapshotUnsubscribe: Unsubscribe | null = null;

    // Step 1: Get all existing data first
    console.log('Fetching existing app review notifications...');

    let existingSnapshot;
    try {
      // First try the most basic getDocs call
      console.log('Trying basic getDocs call...');
      let recentSnapshot;

      // Since orderBy('timestamp') filters out docs without timestamp field,
      // let's use a different approach: get recent documents and sort manually
      try {
        const docIdQuery = query(notificationsCollection);

        recentSnapshot = await getDocs(docIdQuery);
        if (!recentSnapshot.empty) {
          console.log(
            'Documents by ID (should be in descending order):',
            recentSnapshot.docs.map(doc => ({
              id: doc.id,
              timestamp: doc.data().timestamp,
              status: doc.data().status,
            }))
          );

          // Verify the order is correct (largest ID should be first)
          const firstId = parseInt(recentSnapshot.docs[0].id);
          const lastId = parseInt(recentSnapshot.docs[recentSnapshot.docs.length - 1].id);
          console.log('Order verification:', {
            firstId,
            lastId,
            isCorrectOrder: firstId > lastId,
            message:
              firstId > lastId ? 'Correct descending order' : 'Wrong order - may need manual sort',
          });
        } else {
          console.log('Document ID query returned empty');
        }
      } catch (docIdError) {
        // Check if it's a permission error
        if (docIdError instanceof Error && docIdError.message.includes('Missing or insufficient permissions')) {
          console.warn('Firebase permission denied for app review notifications. This is expected for some users.');
          existingSnapshot = null;
          return {
            unsubscribe: () => {
              console.log('No-op unsubscribe (permission denied)');
            },
          };
        }
        console.log(docIdError)
        recentSnapshot = await getDocs(notificationsCollection);
      }

      if (!recentSnapshot.empty) {
        const allDocs = recentSnapshot.docs;
        console.log('Retrieved documents count:', allDocs.length);

        const latestDoc = allDocs[allDocs.length - 1];

        // Create a result with only the latest document
        existingSnapshot = {
          empty: false,
          size: 1,
          docs: [latestDoc],
        };
      } else {
        existingSnapshot = recentSnapshot;
      }
    } catch (error) {
      // Check if it's a permission error
      if (error instanceof Error && error.message.includes('Missing or insufficient permissions')) {
        console.warn('Firebase permission denied for app review notifications. This is expected for some users.');
        return {
          unsubscribe: () => {
            console.log('No-op unsubscribe (permission denied)');
          },
        };
      }
      console.error('Error fetching notifications:', error);
      if (error instanceof Error) {
        console.error('Error details:', {
          message: error.message,
          stack: error.stack,
        });
      }
      // Set empty snapshot to continue with real-time listener
      existingSnapshot = null;
    }

    if (existingSnapshot && !existingSnapshot.empty) {
      console.log('Found existing notifications:', existingSnapshot.size);

      // Get the latest notification
      const latestDoc = existingSnapshot.docs[0];
      const latestData = latestDoc.data();
      const latestNotification: AppReviewNotification = {
        id: latestDoc.id,
        message: latestData.message,
        timestamp: latestData.timestamp || Date.now(),
        status: latestData.status || 'read',
      };

      console.log('Latest notification:', latestNotification);

      // If the latest notification status is unread, trigger it
      if (latestNotification.status === 'unread') {
        console.log('Triggering latest unread notification:', latestNotification.id);
        onNotification?.(latestNotification);
      }
    } else {
      console.log(
        'No existing notifications found, setting up real-time listener for future notifications'
      );

      // Step 2: If no existing data, set up onSnapshot for future notifications only
      const currentTime = Timestamp.now();
      const futureQuery = query(notificationsCollection, where('timestamp', '>', currentTime));

      onSnapshotUnsubscribe = onSnapshot(
        futureQuery,
        (snapshot: QuerySnapshot) => {
          if (!isActive) {
            console.log('Listener is inactive, ignoring snapshot');
            return;
          }

          snapshot.docChanges().forEach((change: DocumentChange) => {
            if (!isActive) {
              console.log('Listener is inactive, ignoring change');
              return;
            }

            console.log('App review notification change:', change);
            if (change.type === 'added') {
              const data = change.doc.data();
              const notification: AppReviewNotification = {
                id: change.doc.id,
                message: data.message,
                timestamp: data.timestamp || Date.now(),
                status: data.status || 'unread',
              };

              console.log('Triggering new notification from real-time listener:', notification.id);
              onNotification?.(notification);
            }
          });
        },
        (error: FirestoreError) => {
          // Check if it's a permission error
          if (error.code === 'permission-denied' || error.message.includes('Missing or insufficient permissions')) {
            console.warn('Firebase real-time listener permission denied. This is expected for some users.');
            // Unsubscribe to prevent further errors
            if (onSnapshotUnsubscribe) {
              onSnapshotUnsubscribe();
              onSnapshotUnsubscribe = null;
            }
            return;
          }
          console.error('Error listening for app review notifications:', error);
          onError?.(error);
        }
      );
    }

    // Return enhanced unsubscribe function
    return {
      unsubscribe: () => {
        console.log('Unsubscribing app review listener');
        isActive = false;
        if (onSnapshotUnsubscribe) {
          onSnapshotUnsubscribe();
        }
      },
    };
  } catch (error) {
    // Check if it's a permission error
    if (error instanceof Error && error.message.includes('Missing or insufficient permissions')) {
      console.warn('Firebase setup permission denied for app review listener. This is expected for some users.');
      return {
        unsubscribe: () => {
          console.log('No-op unsubscribe (setup permission denied)');
        },
      };
    }
    console.error('Error setting up app review listener:', error);
    throw error;
  }
}
